import { Download } from "lucide-react";
import EXTERNAL_LINKS from "../../links";
import { store } from "../../app/store";
import { setApplicationVersionInfo } from "../../features/states/slices";
import useDownloadPageModel from "../../models/download/useDownloadPageModel";
import { isHostedVuplexEnvironment } from "../../routes/vuplexRouter";
import "./AnimotiveVersionActions.module.css";

function createApplicationVersionInfo() {
    return {
        versionNumber: null,
        identifier: null,
        downloadLink: EXTERNAL_LINKS.website.home,
    };
}

function parseVersionParts(versionValue) {
    if (typeof versionValue !== "string" && typeof versionValue !== "number") {
        return null;
    }

    const normalizedValue = String(versionValue).trim();
    if (!normalizedValue.length) {
        return null;
    }

    const parsedParts = normalizedValue.split(".").map((part) => {
        if (!/^\d+$/.test(part)) {
            return Number.NaN;
        }

        return Number(part);
    });

    return parsedParts.some(Number.isNaN) ? null : parsedParts;
}

function compareVersionParts(currentParts, requiredParts) {
    const maxLength = Math.max(currentParts.length, requiredParts.length);

    for (let index = 0; index < maxLength; index += 1) {
        const currentPart = currentParts[index] ?? 0;
        const requiredPart = requiredParts[index] ?? 0;

        if (currentPart > requiredPart) {
            return 1;
        }

        if (currentPart < requiredPart) {
            return -1;
        }
    }

    return 0;
}

function getRequiredVersion(versionInfo, env) {
    const identifier = versionInfo.identifier?.toLowerCase() ?? null;

    switch (identifier) {
        case null:
            return env.REACT_APP_LATEST_VERSION ?? null;
        default:
            return null;
    }
}

export function parseApplicationVersionInfo(value) {
    const versionInfo = createApplicationVersionInfo();

    if (typeof value !== "string") {
        versionInfo.versionNumber = value;
        return versionInfo;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue.length) {
        return versionInfo;
    }

    const splitString = trimmedValue.split(/\s+/, 2);
    versionInfo.versionNumber = splitString[0];
    versionInfo.identifier = splitString[1] ?? null;

    if (versionInfo.identifier?.toLowerCase() === "beta") {
        versionInfo.downloadLink = EXTERNAL_LINKS.downloads.app;
    }

    return versionInfo;
}

export function resolveApplicationVersionStatus({
    currentVersion,
    isHostedVuplex = isHostedVuplexEnvironment(),
    env = process.env,
}) {
    const versionInfo = parseApplicationVersionInfo(currentVersion);

    if (!isHostedVuplex) {
        return {
            isLatestVersion: true,
            versionInfo,
        };
    }

    if (versionInfo.versionNumber === null) {
        return {
            isLatestVersion: null,
            versionInfo,
        };
    }

    const requiredVersion = getRequiredVersion(versionInfo, env);
    const currentVersionParts = parseVersionParts(versionInfo.versionNumber);
    const requiredVersionParts = parseVersionParts(requiredVersion);

    if (!currentVersionParts || !requiredVersionParts) {
        return {
            isLatestVersion: null,
            versionInfo,
        };
    }

    return {
        isLatestVersion: compareVersionParts(currentVersionParts, requiredVersionParts) >= 0,
        versionInfo,
    };
}

export function shouldShowAnimotiveUpdatePage({ isHostedVuplex, isLatestVersion }) {
    return Boolean(isHostedVuplex) && isLatestVersion === false;
}

export async function checkApplicationVersion() {
    const currentVersion = store.getState().OpenInUnity.applicationVersion;
    const { isLatestVersion, versionInfo } = resolveApplicationVersionStatus({ currentVersion });

    store.dispatch(setApplicationVersionInfo(versionInfo));
    return isLatestVersion;
}

export function AnimotiveDownloadLatestButton() {
    const { openDownloadLink } = useDownloadPageModel();

    return (
        <button type={"button"} className={"animotive-update-button"} onClick={openDownloadLink}>
            <Download size={16} aria-hidden={"true"} />
            <span className={"animotive-update-button__label"}>Update available</span>
        </button>
    );
}
